import { Link } from "react-router-dom";
import { ArrowRight, Activity, HeartPulse, ShieldAlert, Users, BarChart3, MessageCircleHeart } from "lucide-react";
import Seo from "../lib/seo";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PetitionCTA from "../components/PetitionCTA";

const AED_IMG = "https://images.pexels.com/photos/17527783/pexels-photo-17527783.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const RESOURCES = [
    { icon: Activity, title: "Cardiac Arrest Information", desc: "Learn the facts about cardiac arrest, how it happens, and why immediate action is critical.", link: "https://www.heart.org/en/health-topics/cardiac-arrest" },
    { icon: HeartPulse, title: "CPR & AED Training", desc: "Find training options and step-by-step guides to help you gain life-saving skills.", link: "https://cpr.heart.org/" },
    { icon: ShieldAlert, title: "Emergency Preparedness", desc: "Tips and checklists to help you prepare your home, workplace, or community for emergencies.", link: "https://www.ready.gov/" },
    { icon: Users, title: "Community Resources", desc: "Local programs, support groups, and organizations working to save lives.", link: "/contact" },
    { icon: BarChart3, title: "Statistics & Research", desc: "Explore the latest data, reports, and studies on cardiac arrest and survival rates.", link: "https://www.heart.org/en/about-us/heart-and-stroke-association-statistics" },
    { icon: MessageCircleHeart, title: "Stories & Inspiration", desc: "Real stories from real people reminding us why this mission matters so much.", link: "/our-story" },
];

export default function ResourcesPage() {
    return (
        <>
            <Seo
                title="Resources & Education — CPR, AEDs, and Emergency Preparedness"
                description="Trusted resources, tools, and information to help you be ready, respond with confidence, and save lives. Knowledge. Preparation. Lives saved."
                path="/resources"
                image={AED_IMG}
            />

            <PageHeader
                overline="Resources"
                title="Knowledge. Preparation. Lives saved."
                subtitle="Education and preparedness can make all the difference when every second counts. Explore trusted resources, tools, and information to help you be ready, respond with confidence, and save lives."
                image={AED_IMG}
            />

            {/* CPR Steps */}
            <section className="bg-editorial py-24 md:py-28" data-testid="resources-cpr-steps">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
                    <Reveal className="lg:col-span-5">
                        <p className="overline mb-5 text-coral">Hands-Only CPR</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-[1.05]">
                            If you witness sudden collapse, act fast.
                        </h2>
                        <p className="mt-6 text-lg text-navy-800/75 leading-relaxed">
                            For untrained bystanders, the American Heart Association recommends Hands-Only CPR. Two steps. Anyone can do it.
                        </p>
                    </Reveal>
                    <Reveal delay={0.1} className="lg:col-span-7">
                        <ol className="space-y-4">
                            {[
                                { n: "01", t: "Call 911 immediately", d: "Or have someone nearby call. Send for the nearest AED." },
                                { n: "02", t: "Push hard and fast", d: "In the center of the chest, 100–120 beats per minute. Don't stop until help arrives." },
                                { n: "03", t: "Use an AED if available", d: "Turn it on, follow the audio prompts. Designed for anyone to use." },
                                { n: "04", t: "Continue until help arrives", d: "Every second of CPR keeps blood and oxygen flowing to the brain." },
                            ].map((s) => (
                                <li key={s.n} className="flex gap-5 p-6 bg-white border border-editorial-border">
                                    <div className="font-serif text-3xl text-coral leading-none">{s.n}</div>
                                    <div>
                                        <h3 className="font-serif text-xl text-navy-900 mb-1">{s.t}</h3>
                                        <p className="text-navy-800/70">{s.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ol>
                    </Reveal>
                </div>
            </section>

            {/* Resource Grid */}
            <section className="bg-editorial-muted py-24" data-testid="resources-grid">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <Reveal className="max-w-3xl mb-12">
                        <p className="overline mb-5 text-coral">Explore</p>
                        <h2 className="font-serif text-4xl md:text-5xl text-navy-900 leading-tight">A library of life-saving resources.</h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-editorial-border">
                        {RESOURCES.map((r, i) => {
                            const isExternal = r.link.startsWith("http");
                            const Tag = isExternal ? "a" : Link;
                            const props = isExternal ? { href: r.link, target: "_blank", rel: "noopener noreferrer" } : { to: r.link };
                            return (
                                <Reveal key={i} delay={i * 0.05}>
                                    <Tag {...props} data-testid={`resource-card-${i}`} className="group block bg-editorial-muted hover:bg-white p-8 lg:p-10 transition-colors h-full">
                                        <r.icon className="w-8 h-8 text-coral mb-6" />
                                        <h3 className="font-serif text-2xl text-navy-900 mb-3 leading-snug">{r.title}</h3>
                                        <p className="text-navy-800/70 mb-6">{r.desc}</p>
                                        <span className="inline-flex items-center gap-2 text-sm font-medium text-navy-900 group-hover:gap-3 transition-all">
                                            View resources <ArrowRight className="w-4 h-4" />
                                        </span>
                                    </Tag>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            <PetitionCTA />
        </>
    );
}
